<template>
  <section class="bg-light py-4">
    <!-- Navigation -->
    <div class="row pb-3">
      <div class="col">
        <router-link to="/rewards" class="text-primary-link">
          {{ $t('message.rewards.title') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        <router-link to="/user-rewards" class="text-primary-link">
          {{ $t('message.rewards.my_redeemed_rewards') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        {{ $t('message.rewards.delivery_info') }}
      </div>
    </div>
    
    <!-- User Reward Delivery Info -->
    <div class="row" v-if="userReward">
      <div class="col-lg-6 offset-lg-3">
        <!-- Email & tel -->
        <div class="card" v-if="userReward.reward.delivery_info_type === 'email_tel'">
          <img
            class="card-img-top"
            :src="userReward.reward.image_url"
            :alt="userReward.reward.title_th"
          />
          <div class="card-body">
            <div class="text-remark mb-2 text-center">
              {{ $t('message.rewards.delivery_info_form_title') }}
            </div>
            <div class="text-center">
              <b>{{ ($i18n.locale === 'th') ? userReward.reward.title_th : userReward.reward.title_en }}</b>
            </div>
            <div class="py-2">
              <div class="form-group">
                <label for="contactTel">{{ $t('message.shipping_address.tel') }}</label>
                <input
                  type="tel"
                  class="form-control"
                  :class="{'form-alert': errors.has('contactTel')}"
                  id="contactTel"
                  name="contactTel"
                  placeholder="080-000-0000"
                  v-validate="'required'"
                  v-model="contactTel"
                />
                <div class="mt-1 text-remark -status-alert" v-if="errors.has('contactTel')">
                  {{ $t('message.required_field') }}
                </div>
              </div>
              <div class="form-group">
                <label for="contactEmail">{{ $t('message.shipping_address.email') }}</label>
                <input
                  type="email"
                  class="form-control"
                  :class="{'form-alert': errors.has('contactEmail')}"
                  id="contactEmail"
                  name="contactEmail"
                  placeholder="name@example.com"
                  v-validate="'required'"
                  v-model="contactEmail"
                />
                <div class="mt-1 text-remark -status-alert" v-if="errors.has('contactEmail')">
                  {{ $t('message.required_field') }}
                </div>
              </div>
            </div>
            <button class="btn btn-info btn-block" 
                    :disabled="isLoading"
                    @click="saveDeliveryInfo()">
              {{ isLoading ? $t('message.button.loading') : $t('message.button.save') }}
            </button>
          </div>
        </div>

        <!-- Full address -->
        <div class="card" v-if="userReward.reward.delivery_info_type === 'full_address'">
          <div class="card-body">
            <!-- name & tel -->
            <div class="form-group">
              <label for="inputName">{{ $i18n.t('message.shipping_address.full_name') }}</label> <span class="text-danger">*</span>
              <input
                type="text"
                class="form-control"
                :class="{'form-alert': errors.has('contactName')}"
                id="inputName"
                name="contactName"
                placeholder=""
                v-model="userShippingAddress.name"
                v-validate="'required'"
              />
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('contactName')">
                {{ $t('message.required_field') }}
              </div>
            </div>
            <div class="form-group">
              <label for="inputTel">{{ $i18n.t('message.shipping_address.tel') }}</label> <span class="text-danger">*</span>
              <input
                type="tel"
                class="form-control"
                :class="{'form-alert': errors.has('contactTel')}"
                id="inputTel"
                name="contactTel"
                placeholder="080-000-0000"
                v-model="userShippingAddress.tel"
                v-validate="'required'"
              />
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('contactTel')">
                {{ $t('message.required_field') }}
              </div>
            </div>

            <!-- building type -->
            <div class="form-group">
              <label>{{ $i18n.t('message.shipping_address.address') }}</label> <span class="text-danger">*</span>
              <div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="radioBuildingType"
                    id="radioBuildingType1"
                    :value="1"
                    v-model="userShippingAddress.building_type"
                  />
                  <label class="form-check-label" for="radioBuildingType1">
                    {{ $i18n.t('message.shipping_address.type_address_home') }}
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="radioBuildingType"
                    id="radioBuildingType2"
                    :value="2"
                    v-model="userShippingAddress.building_type"
                  />
                  <label class="form-check-label" for="radioBuildingType2">
                    {{ $i18n.t('message.shipping_address.type_address_condo') }}
                  </label>
                </div>
              </div>
            </div>

            <!-- address -->
            <div class="form-group" v-if="isCondo()">
              <label for="inputVillage">{{ $i18n.t('message.shipping_address.village')}}</label> <span class="text-danger">*</span>
              <input type="text" 
                     class="form-control" 
                     :class="{'form-alert': errors.has('inputVillage')}"
                     id="inputVillage" 
                     name="inputVillage"
                     key="inputVillage"
                     v-model="userShippingAddress.village" 
                     v-validate="'required'">
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputVillage')">
                {{ $t('message.required_field') }}
              </div>
            </div>
            <div class="form-group" v-if="isCondo()">
              <label for="inputRoomNo">{{ $i18n.t('message.shipping_address.room_no') }}</label>
              <input type="text" 
                     class="form-control" 
                     id="inputRoomNo" 
                     v-model="userShippingAddress.room_no">
            </div>
            <div class="form-group">
              <label for="inputHouseNo">{{ $i18n.t('message.shipping_address.house_no') }}</label> <span class="text-danger">*</span>
              <input type="text" 
                     class="form-control" 
                     :class="{'form-alert': errors.has('inputHouseNo')}"
                     id="inputHouseNo" 
                     name="inputHouseNo"
                     key="inputHouseNo"
                     v-model="userShippingAddress.house_no" 
                     v-validate="'required'">
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputHouseNo')">
                {{ $t('message.required_field') }}
              </div>
            </div>
            <div class="form-group" v-if="!isCondo()">
              <label for="inputVillageNo">{{ $i18n.t('message.shipping_address.village_no') }}</label> <span class="text-danger">*</span>
              <input type="text" 
                     class="form-control" 
                     :class="{'form-alert': errors.has('inputVillageNo')}"
                     id="inputVillageNo" 
                     name="inputVillageNo" 
                     key="inputVillageNo"
                     v-model="userShippingAddress.village_no" 
                     v-validate="'required'">
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputVillageNo')">
                {{ $t('message.required_field') }}
              </div>
            </div>
            <div class="form-group">
              <label for="inputSoi">{{ $i18n.t('message.shipping_address.soi') }}</label>
              <input type="text" class="form-control" id="inputSoi" v-model="userShippingAddress.soi">
            </div>
            <div class="form-group">
              <label for="inputRoad">{{ $i18n.t('message.shipping_address.road') }}</label> <span class="text-danger">*</span>
              <input type="text" 
                     class="form-control" 
                     :class="{'form-alert': errors.has('inputRoad')}"
                     id="inputRoad" 
                     name="inputRoad" 
                     v-model="userShippingAddress.road" 
                     v-validate="'required'">
              <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputRoad')">
                {{ $t('message.required_field') }}
              </div>
            </div>

            <!-- provinces, districts & sub districts -->
            <div class="form-group">
              <label for="inputAddressProvince">{{ $i18n.t('message.shipping_address.province') }}</label> <span class="text-danger">*</span>
                <div>
                  <select class="form-control" 
                          v-model="userShippingAddress.address_province_id" 
                          @change="changeAddressProvince(userShippingAddress.address_province_id)"
                          id="inputAddressProvince"
                          name="inputAddressProvince"
                          :class="{ 'form-alert': errors.has('inputAddressProvince') }"
                          v-validate="'required'">
                    <option :value="null">{{ $i18n.t('message.booking.contact_step.set_province') }}</option>
                    <option :value="addressProvince.id" v-for="addressProvince in sortedProvinces">
                      {{ addressProvince | multiLangName }}
                    </option>
                  </select>
                  <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputAddressProvince')">
                    {{ $t('message.required_selection') }}
                  </div>
                </div>
            </div>
            <div class="form-group">
              <label for="inputDistrict">{{ $i18n.t('message.shipping_address.district') }}</label> <span class="text-danger">*</span>
              <div>
                <select class="form-control" 
                        v-model="userShippingAddress.district_id" 
                        @change="changeDistrict(userShippingAddress.district_id)"
                        id="inputDistrict"
                        name="inputDistrict"
                        :class="{ 'form-alert': errors.has('inputDistrict') }"
                        v-validate="'required'">
                  <option :value="null">{{ $i18n.t('message.booking.contact_step.set_district') }}</option>
                  <option :value="district.id" v-for="district in sortedDistricts">
                    {{ district | multiLangName }}
                  </option>
                </select>
                <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputDistrict')">
                  {{ $t('message.required_selection') }}
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="inputSubDistrict">{{ $i18n.t('message.shipping_address.zip_code') }}</label> <span class="text-danger">*</span>
              <div>
                <select class="form-control" 
                        v-model="userShippingAddress.sub_district_id" 
                        id="inputSubDistrict"
                        name="inputSubDistrict"
                        :class="{ 'form-alert': errors.has('inputSubDistrict') }"
                        v-validate="'required'">
                  <option :value="null">{{ $i18n.t('message.booking.contact_step.set_zip_code') }}</option>
                  <option :value="subDistrcict.id" v-for="subDistrcict, index in sortedSubDistricts">
                    {{ subDistrcict.zip_code }} - {{ subDistrcict | multiLangName }} 
                  </option>
                </select>
                <div class="mt-1 text-remark -status-alert" v-if="errors.has('inputSubDistrict')">
                  {{ $t('message.required_selection') }}
                </div>
              </div>
            </div>

            <!-- save button -->
            <button class="btn btn-info btn-block" 
                    :disabled="isLoading"
                    @click="saveDeliveryInfo()">
              {{ isLoading ? $t('message.button.loading') : $t('message.button.save') }}
            </button>

          </div>
        </div>
      </div>
    </div>

    <loader v-if="isLoadingUserShippingAddress"></loader>

    <!-- Success modal -->
    <b-modal :hide-footer="true" 
             :hide-header="true"
             v-model="showModal">
      <div class="text-center" v-if="userReward">
        <img class="img-fluid rounded" 
             :src="userReward.reward.image_url" 
             :alt="userReward.reward.title_th"/>
        <div class="pt-3">
          {{ $t('message.rewards.contact_success01') }}
        </div>
        <div class="py-3">
          <b>{{ $t('message.rewards.contact_success02') }}</b>
        </div>
        <div>
          <button class="btn btn-info btn-block" @click="closeModal()">
            {{ $t('message.button.close') }}
          </button>
        </div>
      </div>
    </b-modal>

  </section>
</template>


<script>
  import axios from 'axios'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Loader from '../loader/Loader'
  
  export default {
    components: {
      Loader
    },
    data () {
      return {
        id: this.$route.params.id,
        contactTel: null,
        contactEmail: null,
        userShippingAddress: {
          user_id: 0,
          name: null,
          tel: null,
          building_type: 1,
          village: null,
          room_no: null,
          house_no: null,
          village_no: null,
          soi: null,
          road: null,
          address_province_id: null,
          district_id: null,
          sub_district_id: null,
          zip_code: null
        },
        sortBy: (this.$i18n.locale === 'th') ? 'name_th' : 'name_en',
        isLoading: false,
        showModal: false,
        prevRoute: null,
        isLoadingUserShippingAddress: false
      }
    },
    computed: {
      ...mapGetters({
        user: 'auth/getUser'
      }),
      ...mapState('userReward', ['userReward']),
      ...mapState('addressProvince', ['addressProvinces', 'districts', 'subDistricts']),
      sortedProvinces () {
        return this._.orderBy(this.addressProvinces, [this.sortBy], ['asc'])
      },
      sortedDistricts () {
        return this._.orderBy(this.districts, [this.sortBy], ['asc'])
      },
      sortedSubDistricts () {
        return this._.orderBy(this.subDistricts, ['zip_code'], ['asc'])
      }
    },
    methods: {
      ...mapActions('userReward', ['fetchUserRewardDetail']),
      isCondo () {
        return this.userShippingAddress.building_type === 2
      },
      changeAddressProvince (id) {
        this.fetchDistricts(id)
        this.userShippingAddress.district_id = null
        this.userShippingAddress.sub_district_id = null
      },
      changeDistrict (id) {
        this.fetchSubDistricts(id)
        this.userShippingAddress.sub_district_id = null
      },
      fetchAddressProvinces () {
        this.$store.dispatch('addressProvince/LOAD_ADDRESS_PROVINCES')
      },
      fetchDistricts (provinceId) {
        if (provinceId) {
          const queryData = {
            province_id: provinceId
          }
          this.$store.dispatch('addressProvince/LOAD_DISTRICTS', queryData)
        }
      },
      fetchSubDistricts (districtId) {
        if (districtId) {
          const queryData = {
            district_id: districtId
          }
          this.$store.dispatch('addressProvince/LOAD_SUB_DISTRICTS', queryData)
        }
      },
      async fetchUserShippingAddress () {
        const url = `users/${this.user.id}/shipping-address`
        const { data } = await axios.get(url)
        if (!data.error) {
          if (data.user_shipping_address) {
            const userShippingAddress = data.user_shipping_address
            this.userShippingAddress = {
              user_id: userShippingAddress.user_id,
              name: userShippingAddress.name,
              tel: userShippingAddress.tel,
              building_type: userShippingAddress.building_type,
              village: userShippingAddress.village,
              room_no: userShippingAddress.room_no,
              house_no: userShippingAddress.house_no,
              village_no: userShippingAddress.village_no,
              soi: userShippingAddress.soi,
              road: userShippingAddress.road,
              address_province_id: userShippingAddress.address_province_id,
              district_id: userShippingAddress.district_id,
              sub_district_id: userShippingAddress.sub_district_id,
              zip_code: userShippingAddress.zip_code
            }
            this.fetchAddressProvinces()
            this.fetchDistricts(userShippingAddress.address_province_id)
            this.fetchSubDistricts(userShippingAddress.district_id)
          } else {
            this.fetchAddressProvinces()
          }
        } else {
          this.fetchAddressProvinces()
        }
      },
      closeModal () {
        this.showModal = false
        const path = this.prevRoute ? this.prevRoute : '/user-rewards'
        this.$router.replace(path)
      },
      saveDeliveryInfo () {
        const reward = this.userReward.reward
        this.$validator.validate().then(async valid => {
          if (valid) {
            this.isLoading = true
            const url = `user-rewards/${this.id}/delivery-info`
            let submitData = {}
            if (reward.delivery_info_type === 'email_tel') {
              submitData = {
                tel: this.contactTel,
                email: this.contactEmail
              }
            } else {
              this.userShippingAddress.user_id = this.user.id
              submitData = this.userShippingAddress
            }
            const { data } = await axios.post(url, submitData)
            this.isLoading = false
            if (!data.error) {
              this.showModal = true
            }
          }
        })
      }
    },
    async mounted () {
      this.isLoadingUserShippingAddress = true
      await this.fetchUserRewardDetail(this.id)
      if (this.userReward.reward.delivery_info_type === 'full_address') {
        await this.fetchUserShippingAddress()
      } else if (this.userReward.reward.delivery_info_type === 'email_tel') {
        if (this.user) {
          this.contactTel = this.user.tel
          this.contactEmail = this.user.email
        }
      }
      this.isLoadingUserShippingAddress = false
    },
    beforeRouteEnter (to, from, next) {
      next(vm => {
        vm.prevRoute = from.path
      })
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/reward/UserRewardDeliveryInfo.vue